import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, withText } from 'preact-i18n';

import localUtil from '../../lib/util';
import { markRecommendedDuplicates } from '../../lib/raceSignup';

@withText(() => ({
    searchPlaceholder: <Text id='admin.search-users-placeholder'>Search by name, email, or phone...</Text>,
    noResults: <Text id='admin.no-results'>No users found</Text>,
    cancel: <Text id='admin.cancel'>Cancel</Text>,
}))
@observer
class AddRaceClassContestant extends Component {
    constructor(props) {
        super(props);
        this.state = {
            searchText: '',
            searching: false,
            addingUserId: 0,
            addFailed: false,
        };
        this.searchTimer = null;
    }

    searchInput = (e) => {
        const search = e.target.value;
        this.setState({ searchText: search });

        clearTimeout(this.searchTimer);
        if (search.length >= 2) {
            this.searchTimer = setTimeout(async () => {
                this.doSearch();
            }, 500);
        }
    }

    doSearch = async () => {
        const { userStore } = this.props.stores;
        const { searchText } = this.state;
        if (searchText.length >= 2) {
            this.setState({ searching: true });
            await userStore.searchUsersAll({ search: searchText, addSignupStats: true });
            this.setState({ searching: false });
        }
    }

    handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.doSearch();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.onClickCancel();
        }
    }

    onClickAddUser = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('.user-item').dataset;
        const userId = parseInt(id, 10);
        const { race, raceClass, callback } = this.props;
        const { userStore, raceClassContestantStore } = this.props.stores;
        const { addingUserId } = this.state;
        if (addingUserId) {
            return;
        }
        const { searchUsersResult } = userStore;
        const selectedUser = (searchUsersResult || []).find(u => u.id === userId) || {};
        this.setState({ addingUserId: userId, addFailed: false });

        // Manually added contestants are approved right away with no online
        // payment reservation - the organizer handles payment outside the app.
        const response = await raceClassContestantStore.adminCreate({
            user: userId,
            race,
            raceClass,
            firstname: selectedUser.firstname,
            lastname: selectedUser.lastname,
            email: selectedUser.email,
            cellphone: selectedUser.cellphone,
            sportsClub: selectedUser.sportsClub,
            signupDate: new Date().toISOString(),
            approved: true,
        });
        if (response && response.status === 201) {
            if (callback) {
                callback();
            }
            this.onClickCancel();
            return;
        }
        this.setState({ addingUserId: 0, addFailed: true });
    }

    onClickCancel = () => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        appState.toggleDrawer(false, drawerLevel);
    }

    componentDidMount() {
        const { userStore } = this.props.stores;
        // Clear previous search results
        userStore.localUpdateField('searchUsersResult', []);
    }

    render() {
        const { searchText, searching, addingUserId, addFailed } = this.state;
        const { searchPlaceholder, noResults, cancel } = this.props;
        const { raceClassTitle } = this.props;
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const users = markRecommendedDuplicates(userStore.searchUsersResult);

        return (
            <div class='w-100 d-flex flex-column' style='min-height: 100%;'>
                <div class='font-weight-lighter px-3 py-3 box-header d-flex flex-row justify-content-between'>
                    <h5>
                        <i class='fa-solid fa-user-plus me-2' />
                        <Text id='race-class.add-contestant.title'>Add contestant</Text>
                        {raceClassTitle ? <span class='ms-2 text-muted'>{raceClassTitle}</span> : ''}
                    </h5>
                </div>

                <div class='mx-3 mb-2'>
                    <small class='text-muted'>
                        <Text id='race-class.add-contestant.approved-note'>
                            The contestant is added as approved, without online payment.
                        </Text>
                    </small>
                </div>

                <div class={`d-flex flex-column mx-3 px-3 py-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <div class='input-group input-group-sm'>
                        <div class='input-group-text rounded-pill-left'>
                            <i class={`fa-regular fa-magnifying-glass ${searching ? 'fa-spin' : ''}`} />
                        </div>
                        <input
                            class='form-control rounded-pill-right'
                            type='text'
                            value={searchText}
                            placeholder={searchPlaceholder}
                            onInput={this.searchInput}
                            onKeyDown={this.handleKeyDown}
                            autoFocus
                        />
                    </div>
                </div>

                {addFailed && <div class='alert alert-danger mx-3 mt-2 mb-0' role='alert'>
                    <Text id='race-class.add-contestant.error'>Could not add the contestant. Please try again.</Text>
                </div>}

                <div class='flex-fill overflow-auto mx-3 mt-2' style='max-height: calc(100vh - 300px);'>
                    {users && users.length > 0 ? (
                        <div class='d-flex flex-column'>
                            {users.map(u => {
                                const image = localUtil.getImage({ user: u });
                                return (
                                    <div
                                        key={u.id}
                                        class={`user-item d-flex flex-row align-items-center py-2 px-3 mb-1 rounded ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} ${u.recommended ? 'border border-success' : ''}`}
                                        style='cursor: pointer;'
                                        data-id={u.id}
                                        onClick={this.onClickAddUser}
                                    >
                                        <div
                                            class={`${darkmode ? 'bg-secondary' : 'bg-light'} rounded-circle d-flex justify-content-center align-items-center me-3`}
                                            style={`width: 40px; height: 40px; ${image ? `background-image: url('${image}'); background-size: cover;` : ''}`}
                                        >
                                            {!image && (
                                                <span class='font-weight-lighter'>
                                                    {u.firstname ? u.firstname[0] : '?'}
                                                </span>
                                            )}
                                        </div>
                                        <div class='flex-fill'>
                                            <div class='font-weight-bold'>
                                                {u.firstname} {u.lastname}
                                                {u.recommended && (
                                                    <span class='badge bg-success ms-2'>
                                                        <Text id='race-class.add-contestant.recommended'>Recommended</Text>
                                                    </span>
                                                )}
                                            </div>
                                            <small class='text-muted'>
                                                ID: {u.id}
                                                {u.email && <span class='ms-2'>{u.email}</span>}
                                                {u.cellphone && <span class='ms-2'>{u.cellphone}</span>}
                                            </small>
                                            <div>
                                                <span class='badge bg-secondary'>
                                                    {u.signupCount || 0}{' '}
                                                    <Text id='race-class.add-contestant.signups'>signups</Text>
                                                </span>
                                                <span class='badge bg-secondary ms-1'>
                                                    {u.resultCount || 0}{' '}
                                                    <Text id='race-class.add-contestant.results'>results</Text>
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            {addingUserId === u.id
                                                ? <i class='fa-solid fa-spinner fa-spin text-muted' />
                                                : <i class='fa-solid fa-plus text-muted' />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : searchText.length >= 2 && !searching ? (
                        <div class='text-center text-muted py-4'>
                            <i class='fa-solid fa-user-slash fa-2x mb-2' />
                            <div>{noResults}</div>
                        </div>
                    ) : searchText.length > 0 && searchText.length < 2 ? (
                        <div class='text-center text-muted py-4'>
                            <small>
                                <Text id='admin.type-more'>Type at least 2 characters to search</Text>
                            </small>
                        </div>
                    ) : null}
                </div>

                <div class='d-flex flex-row justify-content-end mx-3 my-3'>
                    <button
                        type='button'
                        class='btn btn-outline-secondary rounded-pill'
                        onClick={this.onClickCancel}
                    >
                        <i class='fa-solid fa-times me-2' />
                        {cancel}
                    </button>
                </div>
            </div>
        );
    }
}

export default AddRaceClassContestant;
